import { Calendar } from "lucide-react";
import {
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui";

export type DateRangePreset =
  | "today"
  | "yesterday"
  | "week"
  | "month"
  | "year"
  | "custom";

export interface DateRange {
  preset: DateRangePreset;
  startDate: string;
  endDate: string;
}

interface DateRangeSelectorProps {
  value: DateRange;
  onChange: (value: DateRange) => void;
}

export function DateRangeSelector({ value, onChange }: DateRangeSelectorProps) {
  const handlePresetChange = (preset: DateRangePreset) => {
    if (preset === "custom") {
      onChange({ ...value, preset });

      return;
    }

    onChange({ preset, ...rangeFor(preset) });
  };

  const handleStartChange = (startDate: string) => {
    if (!startDate) {
      return;
    }

    onChange({
      preset: "custom",
      startDate,
      endDate: value.endDate < startDate ? startDate : value.endDate,
    });
  };

  const handleEndChange = (endDate: string) => {
    if (!endDate) {
      return;
    }

    onChange({
      preset: "custom",
      startDate: value.startDate > endDate ? endDate : value.startDate,
      endDate,
    });
  };

  return (
    <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
      <Select
        value={value.preset}
        onValueChange={(v) => handlePresetChange(v as DateRangePreset)}
      >
        <SelectTrigger className="w-40">
          <Calendar className="mr-2 h-4 w-4 text-muted-foreground" />
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="today">Today</SelectItem>
          <SelectItem value="yesterday">Yesterday</SelectItem>
          <SelectItem value="week">Last 7 days</SelectItem>
          <SelectItem value="month">Last 30 days</SelectItem>
          <SelectItem value="year">Last 12 months</SelectItem>
          <SelectItem value="custom">Custom range</SelectItem>
        </SelectContent>
      </Select>
      {value.preset === "custom" && (
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1.5">
            <Label htmlFor="report-start-date" className="text-xs">
              From
            </Label>
            <Input
              id="report-start-date"
              type="date"
              value={value.startDate}
              max={value.endDate}
              onChange={(e) => handleStartChange(e.target.value)}
              className="w-36"
            />
          </div>
          <div className="flex items-center gap-1.5">
            <Label htmlFor="report-end-date" className="text-xs">
              To
            </Label>
            <Input
              id="report-end-date"
              type="date"
              value={value.endDate}
              min={value.startDate}
              onChange={(e) => handleEndChange(e.target.value)}
              className="w-36"
            />
          </div>
        </div>
      )}
    </div>
  );
}

export function rangeFor(preset: Exclude<DateRangePreset, "custom">): {
  startDate: string;
  endDate: string;
} {
  const today = new Date();
  const yyyyMmDd = (d: Date) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");

    return `${y}-${m}-${day}`;
  };

  const end = new Date(today);
  let start = new Date(today);

  if (preset === "today") {
    start = new Date(today);
  } else if (preset === "yesterday") {
    start = new Date(today);
    start.setDate(today.getDate() - 1);
    end.setDate(today.getDate() - 1);
  } else if (preset === "week") {
    start = new Date(today);
    start.setDate(today.getDate() - 6);
  } else if (preset === "month") {
    start = new Date(today);
    start.setDate(today.getDate() - 29);
  } else if (preset === "year") {
    start = new Date(today);
    start.setFullYear(today.getFullYear() - 1);
  }

  return { startDate: yyyyMmDd(start), endDate: yyyyMmDd(end) };
}
